import { useEffect, useRef, useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { BRAND_IMAGE_BUCKET, makeBrandImagePath, prepareBrandImage } from "@/lib/brand/imageUpload";

interface Props {
  brandId: string;
  label: string;
  value: string;
  onChange: (url: string) => void;
  id?: string;
  allowIcon?: boolean;
  onUploadingChange?: (uploading: boolean) => void;
}

export function BrandImageLinkInput({ brandId, label, value, onChange, id, allowIcon = false, onUploadingChange }: Props) {
  const picker = useRef<HTMLInputElement>(null);
  const active = useRef(true);
  const busy = useRef(false);
  const callbacks = useRef({ onChange, onUploadingChange });
  callbacks.current = { onChange, onUploadingChange };
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  async function upload(file: File) {
    if (busy.current) return;
    busy.current = true;
    setUploading(true);
    setError("");
    callbacks.current.onUploadingChange?.(true);
    try {
      const metadata = await prepareBrandImage(file, allowIcon);
      const path = makeBrandImagePath(brandId, metadata.extension, crypto.randomUUID());
      const { error: uploadError } = await supabase.storage.from(BRAND_IMAGE_BUCKET).upload(path, file, {
        upsert: false, contentType: metadata.contentType, cacheControl: "31536000",
      });
      if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);
      const { data } = supabase.storage.from(BRAND_IMAGE_BUCKET).getPublicUrl(path);
      if (active.current) callbacks.current.onChange(data.publicUrl);
    } catch (cause) {
      if (active.current) setError(cause instanceof Error ? cause.message : "Could not upload this image. Try again.");
    } finally {
      busy.current = false;
      callbacks.current.onUploadingChange?.(false);
      if (active.current) setUploading(false);
    }
  }

  return <div className="min-w-0 flex-1 space-y-2">
    <div className="flex min-w-0 items-center gap-2">
      <Input id={id} aria-label={`${label} link`} type="url" className="min-w-0 flex-1" placeholder="Image link (https://…)" value={value} disabled={uploading} onChange={(event) => { setError(""); onChange(event.target.value); }} />
      <input ref={picker} type="file" className="hidden" aria-label={`${label} file`} accept={`.png,.jpg,.jpeg,.webp,.svg${allowIcon ? ",.ico" : ""}`} onChange={(event) => {
        const file = event.currentTarget.files?.[0];
        event.currentTarget.value = "";
        if (file) void upload(file);
      }} />
      <Tooltip><TooltipTrigger asChild><Button type="button" size="icon" variant="outline" className="shrink-0" disabled={uploading} aria-label={`Upload ${label.toLowerCase()}`} aria-busy={uploading} onClick={() => picker.current?.click()}>
        {uploading ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" /> : <Upload className="h-4 w-4" />}
      </Button></TooltipTrigger><TooltipContent>{uploading ? "Uploading…" : `Upload ${label.toLowerCase()}`}</TooltipContent></Tooltip>
    </div>
    {uploading && <p role="status" className="text-xs text-muted-foreground">Uploading…</p>}
    {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
  </div>;
}